/**
 * Utility functions for managing meta tags in the document head
 * These functions handle creating, updating, and removing meta tags
 * to prevent duplication and ensure proper SEO implementation
 */

import { SchemaMarkup } from '../../../interfaces/seo';

/**
 * Escapes a string for safe use in CSS selectors
 * @param value - The value to escape
 * @returns The escaped value safe for use in CSS selectors
 */
function escapeForSelector(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(value)
    : value.replaceAll(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, String.raw`\$&`);
}

/**
 * Gets or creates a meta tag with the specified name attribute
 * @param document - The document object
 * @param name - The name attribute value
 * @returns The meta element
 */
export function getOrCreateMetaTag(document: Document, name: string): HTMLMetaElement {
  // Escape name value for safe selector usage
  const escapedName = escapeForSelector(name);

  let meta = document.querySelector(`meta[name="${escapedName}"]`) as HTMLMetaElement;

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }

  return meta;
}

/**
 * Gets or creates a meta tag with the specified property attribute (for Open Graph)
 * @param document - The document object
 * @param property - The property attribute value
 * @returns The meta element
 */
export function getOrCreateMetaProperty(document: Document, property: string): HTMLMetaElement {
  // Escape property value for safe selector usage
  const escapedProperty = escapeForSelector(property);

  let meta = document.querySelector(`meta[property="${escapedProperty}"]`) as HTMLMetaElement;

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }

  return meta;
}

/**
 * Sets a meta tag with name attribute
 * @param document - The document object
 * @param name - The name attribute value
 * @param content - The content value
 */
export function setMetaTag(
  document: Document,
  name: string,
  content: string | null | undefined
): void {
  if (content === null || content === undefined || content === '') {
    return;
  }

  const meta = getOrCreateMetaTag(document, name);
  meta.setAttribute('content', content);
}

/**
 * Sets a meta tag with property attribute (for Open Graph)
 * @param document - The document object
 * @param property - The property attribute value
 * @param content - The content value
 */
export function setMetaProperty(
  document: Document,
  property: string,
  content: string | null | undefined
): void {
  if (content === null || content === undefined || content === '') {
    return;
  }

  const meta = getOrCreateMetaProperty(document, property);
  meta.setAttribute('content', content);
}

/**
 * Gets or creates a link tag with the specified rel attribute
 * @param document - The document object
 * @param rel - The rel attribute value
 * @returns The link element
 */
export function getOrCreateLinkTag(document: Document, rel: string): HTMLLinkElement {
  // Escape rel value for safe selector usage
  const escapedRel = escapeForSelector(rel);

  let link = document.querySelector(`link[rel="${escapedRel}"]`) as HTMLLinkElement;

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }

  return link;
}

/**
 * Sets the canonical URL
 * @param document - The document object
 * @param url - The canonical URL
 */
export function setCanonicalUrl(document: Document, url: string | null | undefined): void {
  if (url === null || url === undefined || url === '') {
    return;
  }

  const link = getOrCreateLinkTag(document, 'canonical');
  link.setAttribute('href', url);
}

/**
 * Removes a meta tag by name
 * @param document - The document object
 * @param name - The name attribute value
 */
export function removeMetaTag(document: Document, name: string): void {
  const metaTagList = Array.from(document.getElementsByTagName('meta'));
  for (const meta of metaTagList) {
    if (meta.getAttribute('name') === name) {
      meta.remove();
      break;
    }
  }
}

/**
 * Removes a meta tag by property
 * @param document - The document object
 * @param property - The property attribute value
 */
export function removeMetaProperty(document: Document, property: string): void {
  const metaTagList = Array.from(document.getElementsByTagName('meta'));
  for (const meta of metaTagList) {
    if (meta.getAttribute('property') === property) {
      meta.remove();
      break;
    }
  }
}

/**
 * Adds or updates schema markup (JSON-LD) in the document head
 * Supports single schema or array of schemas for multiple structured data objects
 * Reuses existing script elements to prevent memory leaks
 * @param document - The document object
 * @param data - The schema markup object or array of objects
 * @param id - Optional ID for the script tag (defaults to 'schema-markup')
 */
export function setSchemaMarkup(
  document: Document,
  data: SchemaMarkup | SchemaMarkup[] | Record<string, unknown>,
  id = 'schema-markup'
): void {
  // First, remove ALL existing schema scripts with this base ID
  // This handles the case where we had multiple schemas (array) and now have fewer or none
  removeAllSchemaScripts(document, id);

  // Handle array of schemas
  if (Array.isArray(data)) {
    // Add each schema with indexed ID
    data.forEach((schema, index) => {
      const schemaId = index === 0 ? id : `${id}-${index}`;
      setSingleSchemaMarkup(document, schema, schemaId);
    });
    return;
  }

  // Handle single schema
  setSingleSchemaMarkup(document, data, id);
}

/**
 * Removes all schema scripts with the given base ID
 * Handles both single schema (id) and array schemas (id, id-1, id-2, etc.)
 */
function removeAllSchemaScripts(document: Document, baseId: string): void {
  // Remove base schema
  const baseScript = document.getElementById(baseId);
  if (baseScript) {
    baseScript.remove();
  }

  // Remove indexed schemas (schema-markup-1, schema-markup-2, etc.)
  let index = 1;
  let indexedScript = document.getElementById(`${baseId}-${index}`);
  while (indexedScript) {
    indexedScript.remove();
    index++;
    indexedScript = document.getElementById(`${baseId}-${index}`);
  }
}

/**
 * Internal helper to set a single schema markup
 */
function setSingleSchemaMarkup(
  document: Document,
  data: SchemaMarkup | Record<string, unknown>,
  id: string
): void {
  if (!data || Object.keys(data).length === 0) {
    // Remove script if data is empty
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }
    return;
  }

  // Serialize data with error handling
  let jsonString: string;
  try {
    jsonString = JSON.stringify(data);
  } catch (error) {
    console.error('Failed to stringify schema markup:', error);
    return;
  }

  // Ensure document.head exists
  if (!document.head) {
    console.error('document.head is not available');
    return;
  }

  // Reuse existing script element or create new one
  let script = document.getElementById(id) as HTMLScriptElement;

  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', id);
    document.head.appendChild(script);
  }

  // Update script content
  script.textContent = jsonString;
}

/**
 * @deprecated Use setSchemaMarkup instead
 * Kept for backwards compatibility
 */
export function setStructuredData(
  document: Document,
  data: SchemaMarkup | Record<string, unknown>,
  id = 'schema-markup'
): void {
  setSchemaMarkup(document, data, id);
}

/**
 * Validates and truncates text to a maximum length
 * @param text - The text to validate
 * @param maxLength - The maximum length
 * @param suffix - Optional suffix to add if truncated (defaults to '...')
 * @returns The validated/truncated text
 */
export function validateLength(text: string, maxLength: number, suffix = '...'): string {
  if (!text) return text;

  if (text.length <= maxLength) {
    return text;
  }

  // Handle edge case where maxLength is too small for suffix
  if (maxLength <= suffix.length) {
    return text.substring(0, maxLength);
  }

  return text.substring(0, maxLength - suffix.length) + suffix;
}

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param text - The text to escape
 * @returns The escaped text
 */
function escapeHtml(text: string): string {
  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replaceAll(/[&<>"']/g, (char) => htmlEscapeMap[char]);
}

/**
 * Replaces template variables in a string with XSS protection
 * @param text - The text containing template variables
 * @param variables - Object containing variable names and their values
 * @param sanitize - Whether to sanitize values (default: true for security)
 * @returns The text with variables replaced
 */
export function replaceVariables(
  text: string,
  variables: Record<string, string>,
  sanitize = true
): string {
  if (!text) return text;

  let result = text;

  for (const [key, value] of Object.entries(variables)) {
    if (value) {
      const sanitizedValue = sanitize ? escapeHtml(value) : value;
      result = result.replaceAll(`\${${key}}`, sanitizedValue);
    }
  }

  return result;
}
